import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { IMPACT_METRICS, metricDisplay, monthLabel } from "@/lib/impact";
import { ArrowLeft, Printer } from "lucide-react";
import dayjs from "dayjs";

// Phase 17-D item 7: "Download council pack" — print-styled one-pager
// (reuses the certificate print CSS: only .certificate-print prints).
export default function ImpactPack() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.publicImpact(slug).then(setData).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">No impact page for that area.</div>;
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="impact-pack-page">
      <Seo title={`${data.name} — council pack`} />
      <div className="no-print flex items-center justify-between">
        <Link to={`/impact/${data.slug}`} className="text-sm text-neutral-500 hover:text-forest inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back to {data.name} impact
        </Link>
        <Button data-testid="impact-pack-print-btn" onClick={() => window.print()} className="rounded-full bg-forest hover:bg-forest-hover">
          <Printer className="w-4 h-4 mr-2" /> Print pack
        </Button>
      </div>

      <div className="certificate-print mt-8 bg-white border border-neutral-200 rounded-2xl p-10">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">
              Civic<span className="text-terracotta">.</span>Hearth — community impact pack
            </div>
            <h1 className="font-display text-3xl text-forest mt-2 tracking-tight" data-testid="impact-pack-area">{data.name}</h1>
          </div>
          <div className="text-right text-xs text-neutral-500">
            Generated {dayjs(data.generated_at).format("D MMM YYYY")}<br />civichearth.com/impact/{data.slug}
          </div>
        </div>

        <table className="w-full mt-8 text-sm" data-testid="impact-pack-metrics">
          <tbody>
            {IMPACT_METRICS.map((m) => (
              <tr key={m.key} className="border-t border-neutral-100">
                <td className="py-2.5 text-neutral-600">{m.label}</td>
                <td className="py-2.5 text-right font-display text-xl text-forest">{metricDisplay(data.metrics[m.key])}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[11px] text-neutral-500 mt-2">
          Aggregates only — any figure drawn from fewer than {data.suppress_below} people is shown as “&lt;5”.
        </p>

        {data.top_causes.length > 0 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Top causes</div>
            <div className="text-sm text-neutral-700 mt-2">
              {data.top_causes.map((c) => `${c.cause} (${c.count})`).join(" · ")}
            </div>
          </div>
        )}

        {data.trend.length > 0 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Approved hours — last 12 months</div>
            <table className="w-full mt-2 text-xs">
              <tbody>
                {data.trend.map((t) => (
                  <tr key={t.month} className="border-t border-neutral-100">
                    <td className="py-1.5 text-neutral-600">{monthLabel(t.month)}</td>
                    <td className="py-1.5 text-right text-forest font-medium">
                      {t.suppressed ? "hidden (<5 volunteers)" : `${t.hours}h`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.stories?.length > 0 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">In neighbours' words</div>
            {data.stories.map((s) => (
              <p key={s.id} className="text-sm text-neutral-700 mt-3 italic">
                “{s.text.length > 220 ? `${s.text.slice(0, 220)}…` : s.text}” <span className="not-italic text-neutral-500">— {s.user_name}</span>
              </p>
            ))}
          </div>
        )}

        <div className="mt-10 pt-5 border-t border-neutral-200 text-xs text-neutral-500 flex justify-between flex-wrap gap-2">
          <span>Every hour counts, and gets counted — verified volunteer hours, real neighbours.</span>
          <span>hello@civichearth.com</span>
        </div>
        {/* 2026-07: printed and handed to council officers — URL as visible text */}
        <p className="mt-3 text-xs text-neutral-600" data-testid="pack-professionals-line">
          Working with professionals: link workers and GPs can refer residents for practical
          help in minutes — mediated, consent-first and PII-minimal. Details:
          civichearth.com/for-professionals
        </p>
      </div>
    </div>
  );
}
